import React, { useState, useEffect } from 'react';
import { marketplace, UserIntegrationWithDetails } from '../../services/api';
import { useTranslation } from '../../contexts/TranslationContext';
import UserIntegrationCard from './components/UserIntegrationCard';
import ConfigurationModal from './components/ConfigurationModal';
import ConfirmationModal from './components/ConfirmationModal';
import './MyIntegrations.css';
import { notifyUserIntegrationsChanged } from './DidarAdvancedSettings';
import { TourProvider } from '@reactour/tour';
import { useTour } from '@reactour/tour';
import { useGlobalTour } from '../../contexts/TourContext';
import { useTranslatedSteps } from '../stepsTour';
import { enqueueSnackbar } from 'notistack';
import VideoProvider from '../../components/VideoProvider';
import { videoUrls, videoTitles } from '../../utils/videoUrls';

interface MyIntegrationsContentProps {
  onBrowseMarketplace?: () => void;
}

export const MyIntegrationsContent: React.FC<MyIntegrationsContentProps> = ({ onBrowseMarketplace }) => {
  const { setIsOpen } = useTour();
  const { setTourFunction } = useGlobalTour();
  const { t } = useTranslation();
  const [userIntegrations, setUserIntegrations] = useState<UserIntegrationWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<UserIntegrationWithDetails | null>(null);
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [integrationToDelete, setIntegrationToDelete] = useState<number | null>(null);

  useEffect(() => {
    fetchUserIntegrations();
  }, [page, activeFilter]);

  useEffect(() => {
    setTourFunction(() => setIsOpen(true));
  }, [setTourFunction, setIsOpen]);

  const fetchUserIntegrations = async () => {
    try {
      setLoading(true);
      const response = await marketplace.getUserIntegrations({
        page,
        size: 20,
        is_active: activeFilter,
      });
      setUserIntegrations(response.data.items);
      setTotalPages(response.data.total_pages);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch user integrations:', error);
      setError(t('marketplace.myIntegrations.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (userIntegrationId: number) => {
    try {
      const response = await marketplace.getUserIntegrationDetail(userIntegrationId);
      setSelectedIntegration(response.data);
      setShowConfigModal(true);
    } catch (error) {
      console.error('Failed to load integration details:', error);
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: t('marketplace.failedToLoadDetails'), type: 'error' }
      }));
    }
  };

  const handleSaveConfig = async (config: any, notes?: string) => {
    if (!selectedIntegration) return;

    try {
      await marketplace.setupIntegration(selectedIntegration.id, {
        configuration: config,
        notes: notes || selectedIntegration.notes,
      });
      
      setShowConfigModal(false);
      setSelectedIntegration(null);
      
      // Show success message
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message: t('marketplace.myIntegrations.configurationSaved'), type: 'success' }
      }));
      
      // Refresh the list
      fetchUserIntegrations();
    } catch (error: any) {
      console.error('Failed to save configuration:', error);
      const message = error.response?.data?.detail || t('marketplace.myIntegrations.configurationSaveError');
      window.dispatchEvent(new CustomEvent('showToast', { 
        detail: { message, type: 'error' }
      }));
    }
  };

  const handleDelete = (userIntegrationId: number) => {
    setIntegrationToDelete(userIntegrationId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!integrationToDelete) return;

    try {
      await marketplace.deleteIntegration(integrationToDelete);
      
        // Show success message
      enqueueSnackbar(t('marketplace.myIntegrations.integrationRemoved'), { variant: 'success', autoHideDuration: 2000, style: { padding: '10px', marginTop: '20px' } });
      
      
      // Immediately remove the item from the list
      setUserIntegrations(prevIntegrations => 
        prevIntegrations.filter(integration => integration.id !== integrationToDelete)
      );
      
      // Refresh the list to ensure consistency
      await fetchUserIntegrations();
      notifyUserIntegrationsChanged();
    } catch (error: any) {
      const message = error.response?.data?.detail || t('marketplace.myIntegrations.failedToRemove');
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 2000, style: { padding: '10px', marginTop: '20px' } });
    } finally {
      setShowDeleteModal(false);
      setIntegrationToDelete(null);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleFilterChange = (filter: boolean | undefined) => {
    setActiveFilter(filter);
    setPage(1); // Reset to first page when filter changes
  };

  if (loading && userIntegrations.length === 0) {
    return (
      <div className="my-integrations-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p className="my-integrations-text-sm">{t('marketplace.myIntegrations.loadingIntegrations')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-integrations-container">
      <div className="my-integrations-header first-step">
        <h2>{t('marketplace.myIntegrations.title')}</h2>
        <p className="my-integrations-text-sm">{t('marketplace.myIntegrations.description')}</p>
      </div>

      <div className="my-integrations-controls">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${activeFilter === undefined ? 'active' : ''}`}
            onClick={() => handleFilterChange(undefined)}
          >
            {t('marketplace.myIntegrations.filters.all')}
          </button>
          <button
            className={`filter-btn ${activeFilter === true ? 'active' : ''}`}
            onClick={() => handleFilterChange(true)}
          >
            {t('marketplace.myIntegrations.filters.active')}
          </button>
          <button
            className={`filter-btn ${activeFilter === false ? 'active' : ''}`}
            onClick={() => handleFilterChange(false)}
          >
            {t('marketplace.myIntegrations.filters.inactive')}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p className="my-integrations-text-sm">{error}</p>
          <button onClick={fetchUserIntegrations} className="retry-button">
            {t('marketplace.myIntegrations.tryAgain')}
          </button>
        </div>
      )}

      <div className="integrations-list">
        {userIntegrations.map((userIntegration) => (
          <UserIntegrationCard
            key={userIntegration.id}
            userIntegration={userIntegration}
            onSetup={handleSetup}
            onDelete={handleDelete}
          />
        ))}
      </div>

      {userIntegrations.length === 0 && !loading && !error && (
        <div className="empty-state">
          <div className="empty-icon">🤝</div>
          <h4>{t('marketplace.myIntegrations.noIntegrationsFound')}</h4>
          <p className="my-integrations-text-sm">
            {activeFilter === true
              ? t('marketplace.myIntegrations.noActiveIntegrations')
              : activeFilter === false
              ? t('marketplace.myIntegrations.noInactiveIntegrations')
              : t('marketplace.myIntegrations.noIntegrationsYet')}
          </p>
          {activeFilter === undefined && (
            <button type="button" className="btn-primary" onClick={onBrowseMarketplace}>
              {t('marketplace.myIntegrations.browseMarketplace')}
            </button>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className="pagination-button"
          >
            {t('marketplace.myIntegrations.pagination.previous')}
          </button>
          
          <div className="pagination-info">
            <span className="my-integrations-text-sm">{t('marketplace.myIntegrations.pagination.pageInfo', { page, totalPages })}</span>
          </div>
          
          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className="pagination-button"
          >
            {t('marketplace.myIntegrations.pagination.next')}
          </button>
        </div>
      )}

      {showConfigModal && selectedIntegration && (
        <ConfigurationModal
          userIntegration={selectedIntegration}
          isOpen={showConfigModal}
          onClose={() => {
            setShowConfigModal(false);
            setSelectedIntegration(null);
          }}
          onSave={handleSaveConfig}
        />
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setIntegrationToDelete(null);
        }}
        onConfirm={confirmDelete}
        title={t('marketplace.myIntegrations.removeIntegration')}
        message={t('marketplace.myIntegrations.removeConfirm')}
        confirmText={t('marketplace.myIntegrations.remove')}
        cancelText={t('marketplace.myIntegrations.cancel')}
        confirmButtonClass="btn-danger"
      />
    </div>
  );
};

const MyIntegrations: React.FC = () => {
  return (
    <TourProvider steps={useTranslatedSteps().steps_myIntegrations}>
      <VideoProvider videoUrl={videoUrls.myIntegrations} videoTitle={videoTitles.myIntegrations}>
        <div style={{ width: '100%', height: '100%', minHeight: 0, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          <MyIntegrationsContent />
        </div>
      </VideoProvider>
    </TourProvider>
  );
};

export default MyIntegrations; 